// The consent contract, and the reason it is a module of its own rather than code inside the
// banner's Stimulus controller.
//
// The banner lives in the application bundle, which every page loads, including the authenticated
// app. posthog-js lives in the analytics bundle, which only the marketing layout loads
// (doc/product-analytics.md §6, §10). A banner that called `posthog.opt_in_capturing()` directly
// would pull 226KB of SDK across that line and put analytics JavaScript on screens full of payout
// figures. So the two halves never reference each other: the banner dispatches DOM events, the
// analytics bundle listens for them, and this file is the only place the event names and the
// status values are written down. Both sides import it, so they cannot drift apart.
//
// Like analytics/posthog_pageviews.js, it imports nothing and touches no globals (every
// collaborator arrives as an argument), so `node --test` runs it without a browser
// (packs/assets/spec/javascript/analytics/consent.test.js).

// The three values posthog.get_explicit_consent_status() returns. `pending` means the visitor has
// never chosen, which is the only state the banner shows in.
export const CONSENT_GRANTED = "granted"
export const CONSENT_DENIED = "denied"
export const CONSENT_PENDING = "pending"

// Banner -> analytics bundle. detail: { choice }.
export const CONSENT_CHOICE_EVENT = "post-hog:consent-choice"

// Banner -> analytics bundle, asking for the current status. Needed because nothing orders the
// two bundles: the banner's controller can connect before posthog has initialised, in which case
// it hears no announcement and has to ask.
export const CONSENT_STATUS_REQUEST_EVENT = "post-hog:consent-status-request"

// Analytics bundle -> banner. detail: { status }. Every mounted controller listens, which is what
// keeps the banner and the privacy page's control showing the same answer without either knowing
// the other exists.
export const CONSENT_STATUS_EVENT = "post-hog:consent-status"

// Marks the event target the bridge is already listening on, for the same reason
// POST_HOG_LISTENER_MARKER exists: Turbo re-evaluates scripts, and a second bridge would apply
// every choice twice.
export const CONSENT_BRIDGE_MARKER = "postHogConsentBridgeRegistered"

// The banner is for visitors who have not answered. A visitor who has answered either way has,
// and re-asking someone who already said no is the behaviour that makes people distrust banners.
export function bannerShouldShow(status) {
  return status === CONSENT_PENDING
}

// Turns a choice into the one posthog call it means, and returns the choice it applied so a
// caller can tell "handled" from "ignored". An unrecognised choice changes nothing rather than
// falling through to a default: guessing wrong here means either recording someone who declined
// or discarding a consent they gave.
export function applyConsentChoice({ choice, consentApi }) {
  if (choice === CONSENT_GRANTED) {
    consentApi.optIn()
    return CONSENT_GRANTED
  }

  if (choice === CONSENT_DENIED) {
    consentApi.optOut()
    return CONSENT_DENIED
  }

  return null
}

// The sentence the privacy page shows for the current status. Labels arrive from the template as
// data-* values (config/locales/analytics.en.yml), so no copy lives in JavaScript. An unknown
// status falls back to the pending wording, which is the only one that claims nothing about a
// choice the visitor may not have made.
export function statusSummaryFor(status, labels) {
  return labels[status] ?? labels[CONSENT_PENDING]
}

// Listens on behalf of the analytics bundle: applies a choice the banner dispatched, then
// announces the resulting status back. Announcing after applying (rather than trusting the
// choice) means the banner reflects what posthog actually decided, including a refusal such as
// posthog ignoring opt_in_capturing under cookieless_mode "always".
//
// Nothing is answered or applied until the returned markConsentApiReady is called, which the
// caller does immediately after posthog.init. That gate is load-bearing rather than tidy.
// posthog derives its consent storage key from the project token, so before init it reads a key
// belonging to no project: it answers "pending" for everyone, including a visitor who rejected
// months ago, and it writes a choice somewhere nothing will ever read it again. The window is
// not a sliver either, because the two ends of it are further apart than they look. Stimulus
// connects the controller (and it asks for the status) at DOMContentLoaded, while Turbo fires
// the turbo:load that initialises posthog only once document.readyState reaches "complete",
// which waits for every image on the page. Ungated, a returning visitor watches the banner they
// already dismissed sit there for the length of the page load, and a click on it is discarded.
//
// A choice made during that window is held rather than dropped, because the buttons on
// /privacy#tracking-choices are visible the whole time and a click that silently does nothing is
// the worse failure.
export function wireConsentBridge({ eventTarget, consentApi, announce }) {
  if (eventTarget[CONSENT_BRIDGE_MARKER]) return () => {}

  eventTarget[CONSENT_BRIDGE_MARKER] = true

  let consentApiIsReady = false
  let heldChoice = null

  const applyAndAnnounce = (choice) => {
    applyConsentChoice({ choice, consentApi })
    announce(consentApi.status())
  }

  eventTarget.addEventListener(CONSENT_CHOICE_EVENT, (event) => {
    if (consentApiIsReady) {
      applyAndAnnounce(event.detail?.choice)
    } else {
      heldChoice = event.detail?.choice
    }
  })

  eventTarget.addEventListener(CONSENT_STATUS_REQUEST_EVENT, () => {
    if (consentApiIsReady) announce(consentApi.status())
  })

  return function markConsentApiReady() {
    consentApiIsReady = true

    if (heldChoice === null) {
      announce(consentApi.status())
      return
    }

    const choice = heldChoice
    heldChoice = null
    applyAndAnnounce(choice)
  }
}
